import React from "react";

import type { ContextGraph } from "../../types/contextGraph";
import {
	LEGEND_ITEMS,
	legendFor,
	legendVisual,
	nodeVisual,
	type NodeVisual,
} from "./contextGraphTheme";

/** A swatch that looks like the card it names: fill, border and accent bar. */
const Swatch: React.FC<{ visual: NodeVisual }> = ({ visual }) => (
	<span
		aria-hidden="true"
		className="inline-flex h-3 w-4 overflow-hidden rounded-sm"
		style={{ background: visual.background, border: `1px solid ${visual.borderColor}` }}
	>
		<span className="h-full w-[3px]" style={{ background: visual.accent }} />
	</span>
);

export interface ContextGraphLegendProps {
	/** What is drawn: the legend names only the paints on this canvas. */
	graph: Pick<ContextGraph, "nodes" | "edges">;
}

/**
 * The mockup's legend row, for THIS canvas.
 *
 * Swatches are painted from `contextGraphTheme` — the same table the cards read
 * — so a palette change moves both or neither; and the rows come from the
 * graph, so a room graph is not captioned with the covenant and verdict
 * families an answer graph has.
 */
const ContextGraphLegend: React.FC<ContextGraphLegendProps> = ({ graph }) => {
	const live = legendFor(graph);
	return (
	<div
		className="flex flex-wrap items-center gap-x-5 gap-y-2 border-t border-gray-200 px-4 py-2.5"
		data-testid="context-graph-legend"
	>
		{live.items.map((item) => (
			<span key={item.label} className="flex items-center gap-1.5">
				{item.kind === "status" ? (
					// The verdicts on the canvas, worst last.
					<span className="inline-flex gap-0.5">
						{live.statuses.map((status) => (
							<Swatch
								key={status}
								visual={nodeVisual({ kind: "status", status, variant: null })}
							/>
						))}
					</span>
				) : (
					<Swatch visual={legendVisual(LEGEND_ITEMS.indexOf(item))} />
				)}
				<span className="text-xs text-text-muted">{item.label}</span>
			</span>
		))}

		{live.showPath && (
		<span className="flex items-center gap-1.5">
			<svg aria-hidden="true" width="24" height="6" viewBox="0 0 24 6">
				<line
					x1="0"
					y1="3"
					x2="24"
					y2="3"
					stroke="var(--cg-path)"
					strokeWidth="2.5"
				/>
			</svg>
			<span className="text-xs font-medium" style={{ color: "var(--cg-path)" }}>
				reasoning path
			</span>
		</span>
		)}
	</div>
	);
};

export default ContextGraphLegend;
